'use client';

/**
 * AvailabilityWidget — live Bookeasy availability + pricing for a campsite.
 *
 * Renders only when the entity has a Bookeasy operator id. The user picks dates;
 * we query /api/bookeasy/operators/<id>/availability and show rooms/prices.
 *
 * Graceful by design: if there's no operator id, no live availability, or the
 * Bookeasy pilot is down, we fall back to the existing "Book Online" CTA — so the
 * page is never worse than before, and lights up automatically once a production
 * ControlID + live data are connected.
 */
import React, { useState } from 'react';
import AvailabilityCalendar from './AvailabilityCalendar';
import { trackAvailabilityCalendarOpen } from '../lib/analytics';

const API = 'https://app.trip-planner.com.au/api';

function addDays(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function nightsBetween(ci: string, co: string): number {
  const a = new Date(ci + 'T00:00:00').getTime();
  const b = new Date(co + 'T00:00:00').getTime();
  const n = Math.round((b - a) / 86_400_000);
  return n >= 1 ? n : 2;
}

function isoPlus(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// Sensible default: the coming weekend (next Friday → Sunday, 2 nights).
// Camping demand is weekend-led, so that's the most useful starting query.
function defaultWeekend(): { checkIn: string; checkOut: string } {
  const ci = new Date();
  ci.setDate(ci.getDate() + 1); // from tomorrow…
  while (ci.getDay() !== 5) ci.setDate(ci.getDate() + 1); // …advance to Friday
  const co = new Date(ci);
  co.setDate(co.getDate() + 2); // Sunday (2 nights)
  return { checkIn: ci.toISOString().slice(0, 10), checkOut: co.toISOString().slice(0, 10) };
}

interface Product {
  product_id?: number;
  name?: string;
  price?: number;
  max_guests?: number;
  available?: boolean;
  image?: string;
}
interface AvailResult {
  source?: string;
  products?: Product[];
  operator_name?: string;
  nights?: number;
  next_available_date?: string;
  price_unit?: string;
}

interface Props {
  bookingUrl?: string | null;
  entityName?: string;
  fallbackImage?: string;   // entity hero — used when a room's Bookeasy photo is missing/broken
}

// The operator id + product live in the Bookeasy booking_url:
//   .../{accom|tours}/{slug}/{operatorId}
function parseBooking(u?: string | null): { product: 'accom' | 'tours'; operatorId: number } | null {
  if (!u) return null;
  const m = u.match(/\/(accom|tours)\/[^/]+\/(\d+)/);
  return m ? { product: m[1] as 'accom' | 'tours', operatorId: Number(m[2]) } : null;
}

/**
 * A room/site photo that never shows a broken-image icon. Some Bookeasy room
 * photos 404 at Bookeasy (e.g. the bookaus chvr* URLs), and the entity hero
 * used as the fallback can be one of those too. Tries the room photo, then
 * the fallback, then leaves the neutral grey tile. Counts attempts rather
 * than comparing img.src (the browser normalises it, so a relative or
 * re-encoded fallback never matched and could retry forever).
 */
export function RoomPhoto({ src, fallback, alt, className }: { src?: string; fallback?: string; alt: string; className: string }) {
  const candidates = [src, fallback].filter((u, i, a): u is string => !!u && a.indexOf(u) === i);
  const [attempt, setAttempt] = useState(0);
  const current = candidates[attempt];
  if (!current) return null;
  return (
    <img
      key={current}
      src={current}
      alt={alt}
      loading="lazy"
      className={className}
      onError={() => setAttempt((n) => n + 1)}
    />
  );
}

export default function AvailabilityWidget({ bookingUrl, entityName, fallbackImage }: Props) {
  const [checkIn, setCheckIn] = useState(() => defaultWeekend().checkIn);
  const [checkOut, setCheckOut] = useState(() => defaultWeekend().checkOut);
  const [adults, setAdults] = useState(2);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AvailResult | null>(null);
  const [checked, setChecked] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);

  // Not a Bookeasy-bookable campsite → render nothing (no broken widget).
  const booking = parseBooking(bookingUrl);
  if (!booking) return null;

  // Calendar is collapsed by default (like Bookeasy's date-picker dropdown);
  // opening it is a strong booking-intent signal, so fire a GA event.
  const toggleCalendar = () => {
    const next = !showCalendar;
    setShowCalendar(next);
    if (next) {
      trackAvailabilityCalendarOpen({
        operatorId: booking.operatorId,
        product: booking.product,
        entityName,
      });
    }
  };

  const check = async (ci: string = checkIn, co: string = checkOut) => {
    setLoading(true);
    setFailed(false);
    setResult(null);
    setChecked(true);
    try {
      const url =
        `${API}/bookeasy/operators/${booking.operatorId}/availability` +
        `?check_in=${ci}&check_out=${co}&adults=${adults}&product=${booking.product}`;
      const res = await fetch(url);
      if (!res.ok) {
        setFailed(true);
        return;
      }
      setResult(await res.json());
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  // Calendar pick → set this date as check-in, preserve the chosen stay length,
  // and immediately price that range.
  const onPickDate = (date: string) => {
    const nights = nightsBetween(checkIn, checkOut);
    const co = addDays(date, nights);
    setCheckIn(date);
    setCheckOut(co);
    check(date, co);
  };

  const allProducts = result?.products || [];
  const products = allProducts.filter((p) => p.available !== false);
  const other = allProducts.filter((p) => p.available === false);
  const hasLive = products.length > 0;
  const hasAny = allProducts.length > 0;
  const unit = result?.price_unit ? `/${result.price_unit}` : '';
  const datesValid = checkIn < checkOut;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:p-8">
      <div className="mb-4 flex items-center gap-2">
        <h2 className="text-2xl font-semibold text-slate-900">Check availability</h2>
        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
          Live pricing
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-medium text-gray-600">
          Check-in
          <input
            type="date"
            value={checkIn}
            min={isoPlus(0)}
            onChange={(e) => setCheckIn(e.target.value)}
            className="mt-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="flex flex-col text-xs font-medium text-gray-600">
          Check-out
          <input
            type="date"
            value={checkOut}
            min={checkIn}
            onChange={(e) => setCheckOut(e.target.value)}
            className="mt-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="flex flex-col text-xs font-medium text-gray-600">
          Guests
          <select
            value={adults}
            onChange={(e) => setAdults(Number(e.target.value))}
            className="mt-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          >
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <button
          onClick={() => check()}
          disabled={loading || !datesValid}
          className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {loading ? 'Checking…' : 'Check'}
        </button>
      </div>

      {/* Collapsed by default (like Bookeasy's date-picker dropdown). Expanding
          reveals a month calendar where bold = bookable; picking a date sets it
          as check-in and prices the stay. */}
      {(booking.product === 'accom' || booking.product === 'tours') && (
        <div className="mt-4">
          <button
            type="button"
            onClick={toggleCalendar}
            aria-expanded={showCalendar}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            {showCalendar ? 'Hide available dates' : 'See available dates'}
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden
              className={`transition-transform ${showCalendar ? 'rotate-180' : ''}`}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {showCalendar && (
            <div className="mt-3">
              <AvailabilityCalendar
                operatorId={booking.operatorId}
                product={booking.product}
                selected={checkIn}
                onPick={onPickDate}
              />
            </div>
          )}
        </div>
      )}

      {/* Available rooms — photo-led cards (the widget sits in the main column) */}
      {!loading && checked && hasLive && (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => (
            <div key={p.product_id ?? i} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <div className="aspect-[4/3] w-full overflow-hidden bg-gray-100">
                <RoomPhoto
                  key={p.image || 'none'}
                  src={p.image}
                  fallback={fallbackImage}
                  alt={p.name || ''}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="p-3">
                <div className="truncate text-sm font-semibold text-gray-900">{p.name || 'Site / room'}</div>
                {p.max_guests ? <div className="text-xs text-gray-500">Sleeps up to {p.max_guests}</div> : null}
                <div className="mt-2 flex items-center justify-between gap-2">
                  {typeof p.price === 'number' ? (
                    <span className="text-sm font-semibold text-gray-900">
                      From ${p.price.toFixed(0)}
                      {unit ? <span className="text-xs font-normal text-gray-500"> {unit}</span> : null}
                    </span>
                  ) : (
                    <span />
                  )}
                  {bookingUrl && (
                    <a
                      href={bookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whitespace-nowrap rounded-md bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-800"
                    >
                      Book
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Available on other dates — sold-out room types, so a full date never loses the customer */}
      {!loading && checked && other.length > 0 && (
        <div className="mt-5">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Available on other dates
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {other.map((p, i) => (
              <a
                key={p.product_id ?? `o${i}`}
                href={bookingUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="group overflow-hidden rounded-xl border border-gray-200 bg-white hover:border-gray-300"
              >
                <div className="aspect-[4/3] w-full overflow-hidden bg-gray-100">
                  <RoomPhoto
                    key={p.image || 'none'}
                    src={p.image}
                    fallback={fallbackImage}
                    alt={p.name || ''}
                    className="h-full w-full object-cover opacity-80 group-hover:opacity-100"
                  />
                </div>
                <div className="p-2.5">
                  <div className="truncate text-xs font-medium text-gray-700">{p.name || 'Site / room'}</div>
                  <div className="mt-1 text-xs font-medium text-emerald-700">Check dates →</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Graceful fallback: outage or no products of any kind */}
      {!loading && checked && !hasAny && (
        <div className="mt-4 rounded-lg bg-gray-50 px-3 py-3 text-sm text-gray-600">
          {failed
            ? "Live pricing isn't loading right now."
            : result?.next_available_date
              ? `Booked out for those dates — next opening ${result.next_available_date}.`
              : 'No openings for those dates.'}
          {bookingUrl && (
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-1 font-semibold text-emerald-700 hover:underline"
            >
              Check available dates →
            </a>
          )}
        </div>
      )}
    </div>
  );
}
